import type { LocationQuery } from 'vue-router'

/**
 * Listing pages are `noindex`d once the visitor refines them, because sort and
 * facet permutations are near-duplicates of the unfiltered page.
 *
 * `page` is deliberately exempt: `?page=2` is a distinct slice of the catalogue,
 * not a duplicate, and it is the only internal crawl path to products past the
 * first screenful. It carries a self-referencing canonical (see
 * `canonicalForPath` in `useSeo.ts`), so it can be indexed on its own terms.
 */
export function hasNoindexQuery(query: LocationQuery): boolean {
  return Object.keys(query).some(key => key !== 'page')
}

/**
 * Whether `haystack` already says `word`, as a whole word and ignoring case.
 *
 * Used to suppress a gender qualifier the collection name already carries:
 * every Vendure collection is named without its audience ("Shorts", cs
 * "Šortky") except `women-sportbra`, which is named "Women Sport Bra" — and
 * "Women Sport Bra for Women" is not a title worth shipping. Whole-word so
 * "Women" cannot be matched by the "Men" test.
 */
export function saysWord(haystack: string, word: string): boolean {
  if (!word)
    return false
  // Unicode-aware boundaries: \b is ASCII-only, so it would fire in the middle
  // of "Dámské" or "mężczyzn". A leading alternation rather than a lookbehind —
  // lookbehind throws a SyntaxError on Safari below 16.4, and this runs on the
  // client too whenever the head recomputes during navigation.
  const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`(^|[^\\p{L}])${escaped}(?![\\p{L}])`, 'iu').test(haystack)
}
